/**
 * KST 시각 헬퍼 — 어드민 MCP 연동 화면 전용
 *
 * 화면은 `<input type="datetime-local">` 로 KST 벽시계 시각('YYYY-MM-DDTHH:mm')을 다루고,
 * 서버는 **시간대가 붙은 ISO** 만 받는다(naive 면 422). 둘 사이를 여기서만 오간다.
 * KST 는 서머타임이 없어 +09:00 고정 오프셋으로 충분하다.
 */
import { parseServerDate } from '@/lib/utils'

const KST_OFFSET_MS = 9 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

/** datetime-local 값 형식 (KST 벽시계, 분 단위) */
export const KST_INPUT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
/** date 값 형식 (YYYY-MM-DD) */
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Date → KST datetime-local 값('YYYY-MM-DDTHH:mm') */
export function toKstInput(d: Date): string {
  return new Date(d.getTime() + KST_OFFSET_MS).toISOString().slice(0, 16)
}

/** KST datetime-local 값 → 서버로 보낼 `+09:00` ISO */
export function kstInputToIso(value: string): string {
  return `${value}:00+09:00`
}

/** KST datetime-local 값 → epoch ms (비교용). 형식이 틀리면 NaN */
export function kstInputToMs(value: string): number {
  if (!KST_INPUT_RE.test(value)) return Number.NaN
  return Date.parse(kstInputToIso(value))
}

/** KST datetime-local 값을 사람이 읽는 형태('YYYY-MM-DD HH:mm')로 */
export function formatKstInput(value: string): string {
  return value.replace('T', ' ')
}

/** 서버 시각(ISO) → 'YYYY-MM-DD HH:mm' (KST). 없으면 '-' */
export function formatKst(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = parseServerDate(iso)
  if (Number.isNaN(d.getTime())) return iso
  return formatKstInput(toKstInput(d))
}

/**
 * 호출 기록 기본 조회 구간 — 최근 7일.
 * 끝은 다음 분 경계로 올린다(구간이 [since, until) 이라 방금 들어온 호출도 포함되게).
 */
export function defaultCallWindow(now: Date = new Date()): { since: string; until: string } {
  const untilMs = Math.ceil(now.getTime() / 60_000) * 60_000
  return {
    since: toKstInput(new Date(untilMs - 7 * DAY_MS)),
    until: toKstInput(new Date(untilMs)),
  }
}
